import React from 'react';
import { Shield, Eye, Cookie, ExternalLink, Database, Flame, X } from 'lucide-react';

export const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen">
      {/* Header — matches AppHeader */}
      <header className="bg-navy-900 border-b border-navy-800 shadow-md">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => window.history.back()}
              className="flex items-center gap-1.5 text-white/60 hover:text-white transition-colors text-sm"
              title="Close this tab"
            >
              <X className="w-4 h-4" />
              <span>Close</span>
            </button>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center shadow-sm">
                <Flame className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-[17px] font-bold text-white font-brand tracking-tight">Privacy Policy</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">

          {/* Privacy Overview */}
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-3">
              <Shield className="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-lg font-semibold text-amber-900 mb-3">Privacy Commitment</h2>
                <p className="text-amber-800">
                  FuelHunt respects your privacy and collects only the minimum information needed to operate and improve the site.
                </p>
              </div>
            </div>
          </div>

          {/* Privacy Policy Content */}
          <div className="prose prose-gray max-w-none">
            <div className="flex items-center gap-2 mb-6">
              <Eye className="w-6 h-6 text-amber-500" />
              <h2 className="text-2xl font-bold text-gray-900 m-0">Privacy Policy</h2>
            </div>

            <div className="space-y-6">
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Database className="w-5 h-5 text-gray-500" />
                  <h3 className="text-lg font-semibold text-gray-900 m-0">Analytics</h3>
                </div>
                <p className="text-gray-700">
                  We may use analytics tools (such as Google Analytics or similar) to collect anonymous usage statistics,
                  such as page visits, browser type, and general geographic region. This helps us understand and improve
                  how users interact with the site.
                </p>
              </section>

              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Cookie className="w-5 h-5 text-gray-500" />
                  <h3 className="text-lg font-semibold text-gray-900 m-0">Cookies</h3>
                </div>
                <p className="text-gray-700">
                  Cookies may be used for essential site functionality or analytics. You can disable cookies in your
                  browser settings if you prefer. We use cookies to:
                </p>
                <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1">
                  <li>Remember your disclaimer acknowledgment</li>
                  <li>Store your vehicle configuration preferences</li>
                  <li>Maintain your search preferences during your session</li>
                  <li>Collect anonymous usage analytics</li>
                </ul>
              </section>

              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="w-5 h-5 text-gray-500" />
                  <h3 className="text-lg font-semibold text-gray-900 m-0">Personal Data</h3>
                </div>
                <p className="text-gray-700">
                  FuelHunt does not collect personally identifiable information (such as names, emails, or payment details)
                  unless explicitly provided through contact forms. We do not require user accounts or registration to use our services.
                </p>
              </section>

              <section>
                <div className="flex items-center gap-2 mb-3">
                  <ExternalLink className="w-5 h-5 text-gray-500" />
                  <h3 className="text-lg font-semibold text-gray-900 m-0">Data Sharing</h3>
                </div>
                <p className="text-gray-700">
                  We do not sell, trade, or share your data with third parties, except where required by law or necessary
                  for operating core services (e.g., hosting, analytics). Any data sharing is limited to:
                </p>
                <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1">
                  <li>Anonymous analytics data to improve our services</li>
                  <li>Technical data required for hosting and security</li>
                  <li>Legal compliance when required by applicable law</li>
                </ul>
              </section>

              <section>
                <div className="flex items-center gap-2 mb-3">
                  <ExternalLink className="w-5 h-5 text-gray-500" />
                  <h3 className="text-lg font-semibold text-gray-900 m-0">External Links</h3>
                </div>
                <p className="text-gray-700">
                  FuelHunt may link to third-party websites (such as government or retailer APIs). We are not responsible
                  for the content or privacy practices of those sites. These external sites include:
                </p>
                <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1">
                  <li>UK Government fuel price data sources</li>
                  <li>Retailer APIs for real-time pricing</li>
                  <li>Mapping and routing services</li>
                  <li>Other fuel price comparison websites</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Location Data</h3>
                <p className="text-gray-700">
                  FuelHunt may request access to your device location to provide nearby fuel stations. This location data is:
                </p>
                <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1">
                  <li>Only used to find nearby fuel stations</li>
                  <li>Not stored on our servers</li>
                  <li>Only accessed with your explicit permission</li>
                  <li>Can be disabled in your browser settings at any time</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Data Storage</h3>
                <p className="text-gray-700">
                  Any data we collect is stored securely and retained only as long as necessary to provide our services.
                  Local storage (browser-based) is used for:
                </p>
                <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1">
                  <li>Vehicle configuration preferences</li>
                  <li>Disclaimer acknowledgment status</li>
                  <li>Search history for improved user experience</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Your Rights</h3>
                <p className="text-gray-700">
                  You have the right to:
                </p>
                <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1">
                  <li>Clear your browser data at any time</li>
                  <li>Disable location services</li>
                  <li>Block cookies in your browser</li>
                  <li>Contact us about any privacy concerns</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Updates to This Policy</h3>
                <p className="text-gray-700">
                  We may update this privacy policy from time to time. Changes will be posted on this page with an updated
                  revision date. Continued use of FuelHunt after changes indicates acceptance of the new policy.
                </p>
              </section>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                For questions about this privacy policy or data handling, please contact us at{' '}
                <a href="mailto:fuelhunt@hotmail.com" className="text-amber-600 hover:text-amber-700">
                  fuelhunt@hotmail.com
                </a>
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Last updated: {new Date().toLocaleDateString('en-GB', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
